import * as rrweb from 'rrweb';
import type {eventWithTime} from '@rrweb/types';
import {URI} from '@/rest/api';
import {createConsoleRecordPlugin} from '@/SessionReplay/consoleRecordPlugin';
import {redactString} from '@/SessionReplay/consoleRedactor';
import {resetAccumulator, buildAccumulatedSummary} from '@/SessionReplay/sessionSummaryAccumulator';
// OWA-675: the masking policy lives in its own dependency-free module so forms
// can unit-test it against their rendered DOM without importing this one.
import {shouldMaskInput} from '@/lib/sessionReplayMasking';
import {tabSession} from '../Auth/session';
import {serverCredential} from '../Auth/credential';
import {SessionChangedError, type SessionOperation} from '../Auth/tabSession';
import {ReplayCaptureQueue} from './sessionReplayCaptureQueue';
import {SessionReplayRuntime} from './sessionReplayRuntime';
import {REPLAY_SESSION_SOFT_BYTES, REPLAY_SESSION_SOFT_CHUNK_SLOTS} from './sessionReplayOutbox';
import {ReplayStateStore, type ReplayCaptureState, type ReplayCreationDescriptor} from './sessionReplayState';
import {ReplayTransportError, replayCredentialMatchesScope, SessionReplayTransport} from '../rest/sessionReplayTransport';

export function isRecordingActive(): boolean {
	return sessionId !== null;
}

// Session storage keys for resuming across page reloads (per-TAB by design —
// each tab records its own rrweb stream; see LS_VISIT_ID below for the
// cross-tab grouping key).
const SS_SESSION_ID = 'replay_sessionId';
const SS_CHUNK_INDEX = 'replay_chunkIndex';

// Local storage keys for the visit — shared across every tab on this origin.
const LS_VISIT_ID = 'replay_visitId';
const LS_VISIT_LAST_ACTIVITY = 'replay_visitLastActivity';

// Singleton state
let sessionId: string | null = null;
let stopFn: (() => void) | null = null;
/** The emit callback of the attached recorder, for events recorded outside
 *  rrweb's own observers (`handleFieldLeft`). */
let emitRecorderEvent: ((event: eventWithTime) => void) | null = null;
let flushInterval: ReturnType<typeof setInterval> | null = null;
let inactivityTimer: ReturnType<typeof setTimeout> | null = null;
let chunkIndex = 0;
let flushing = false;
let flushPromise: Promise<void> | null = null;
let runtime: SessionReplayRuntime | null = null;
let runtimeOperation: SessionOperation | null = null;
let captureQueue: ReplayCaptureQueue | null = null;
let recordingFailure: Error | null = null;
let recordingPaused = false;
let recoveryController: AbortController | null = null;
let recoveryPromise: Promise<void> | null = null;
let rolloverPromise: Promise<void> | null = null;
let snapshotBoundaryEvents: unknown[] | null = null;
// A refused logout freezes this stream until an explicit retry preserves it.
// Ordinary rollover/opt-out paths must not discard its retained events.
let preservationHold = false;

/** Credentials from the first startRecording() call, kept so the session can be
 *  restarted without a page reload (the app only calls startRecording() once,
 *  from setLoggingUser() in remoteLogging.ts). */
let recordingUserId: string | null = null;
let recordingFirmId: string | null = null;
let recordingEpoch = 0;

function recordingKey(key: string): string {
	return `${key}:${recordingFirmId ?? 'unbound'}:${recordingUserId ?? 'unbound'}`;
}

function recordingOperation() {
	const operation = tabSession.capture();
	if (operation.identity.firmId !== recordingFirmId || operation.identity.userId !== recordingUserId) {
		throw new SessionChangedError();
	}
	return operation;
}

tabSession.subscribe(() => {
	if (tabSession.isLocalReady()) return;
	// Stop observing a locked document, retaining already buffered events for
	// this recording's owner. Reauthentication can resume the same recorder.
	stopFn?.();
	stopFn = null;
	disarmRestartOnActivity();
});

/** Timestamp (ms) of the last successful server contact for this session —
 *  a chunk upload or a heartbeat. Drives the heartbeat cadence below. */
let lastServerContact = 0;

/** True while a one-shot activity listener is armed to restart recording after
 *  an inactivity close. Not armed on logout — see stopRecording(). */
let restartArmed = false;
let restartListener: (() => void) | null = null;

/** True while stopRecording() is mid-flight. Its final flush can still get a
 *  403/404 from the server; reacting to those with another stop or a restart
 *  would re-enter teardown or open a new session during logout. */
let stopping = false;

/** True while startRecording() is mid-flight. `sessionId` cannot serve as the
 *  re-entry guard on its own: it is not assigned until after the awaited
 *  resume-check and session POST, so every caller arriving inside that window
 *  passes the `if (sessionId)` check and starts its own rrweb recorder. Only the
 *  last one is reachable through the module-level stopFn, leaving the others'
 *  whole-document MutationObserver, console wrapper and flush interval live for
 *  the lifetime of the tab. Set synchronously before the first await. */
let starting = false;

interface SessionClaim { release(): Promise<void> }

let activeSessionClaim: SessionClaim | null = null;

interface RetiringSession {
	runtime: SessionReplayRuntime;
	queue: ReplayCaptureQueue;
	claim: SessionClaim | null;
	summary: unknown;
	retry?: Promise<void>;
}

// A rollover source remains live until its synchronous capture queue is
// durable and the server has accepted completion. In particular, its Web Lock
// cannot be released while an IndexedDB append failure still leaves events in
// memory in this document.
let retiringSession: RetiringSession | null = null;

function unclaimedSession(): SessionClaim {
	return {release: async () => {}};
}

/**
 * Claim exclusive ownership of a session id for this tab.
 *
 * Existing sessions fail closed when Web Locks is unavailable, because a
 * duplicated tab copied the same sessionStorage. A freshly generated UUID can
 * proceed without a lock because no other document has observed it yet.
 */
async function claimSession(id: string, fresh = false): Promise<SessionClaim | null> {
	if (typeof navigator === 'undefined' || !navigator.locks) return fresh ? unclaimedSession() : null;

	return new Promise<SessionClaim | null>(resolve => {
		let releaseLock: (() => void) | undefined;
		let settled = false;
		// The lock is held until we call release, so the callback parks on a
		// promise we resolve at teardown. `ifAvailable` makes this a test rather
		// than a wait: another tab holding it means a duplicated tab, and we
		// want to know now, not whenever that tab happens to close.
		const done = navigator.locks.request(`replay-session-${id}`, {ifAvailable: true}, lock => {
			if (!lock) {
				settled = true;
				resolve(null);
				return Promise.resolve();
			}
			return new Promise<void>(release => {
				releaseLock = release;
				settled = true;
				let released = false;
				resolve({release: async () => {
					if (!released) {
						released = true;
						releaseLock?.();
					}
					try { await done; }
					catch { /* Rejection still releases the browser lock. */ }
				}});
			});
		});
		void done.catch(() => {
			if (!settled) resolve(fresh ? unclaimedSession() : null);
		});
	});
}

/**
 * Record which tab the user is actually looking at, as rrweb custom events.
 *
 * Playback needs this to follow the active tab across a multi-tab visit
 * (OWA-669). Deriving it later from event density would be guesswork, and the
 * data can only be captured while recording — so capture it now, even though
 * the player that consumes it ships later.
 */
function emitWindowFocusState(): void {
	try {
		rrweb.record.addCustomEvent(document.hasFocus() ? 'windowFocus' : 'windowBlur', {});
	}
	catch {
		// addCustomEvent throws if the recorder isn't attached; nothing to record.
	}
}

function handleWindowFocus(): void {
	touchVisitActivity();
	emitWindowFocusState();
}

/**
 * What the recording keeps of a field's value (OWA-877). Fields the masking
 * policy claims, and a field the user is still typing in, become asterisks:
 * the redactor recognizes a complete SSN but not the partial number typed
 * before its last digit (John, q-20260927-192931-x2bg). Everything else goes
 * through the redactor.
 */
function recordedInputValue(text: string, element: HTMLElement, typing: boolean): string {
	return shouldMaskInput(element) || typing ? '*'.repeat(text.length) : redactString(text);
}

/**
 * Record a field's value as the user leaves it. While the field has focus
 * every recorded update of it is asterisks, and a field can commit while it
 * still has focus (Enter in a text field, a date picked in a date field) with
 * no further `change` on blur, so rrweb would otherwise keep the asterisks.
 * This emits rrweb's own input event for the field, synchronously, into the
 * recorder's emit path, so it is recorded before any stop that follows, and
 * nothing is written to the field.
 */
function handleFieldLeft(event: FocusEvent): void {
	const field = event.target;
	if (!stopFn || !emitRecorderEvent) return;
	if (!(field instanceof HTMLInputElement || field instanceof HTMLTextAreaElement)) return;
	if (field instanceof HTMLInputElement && ['file', 'checkbox', 'radio'].includes(field.type)) return;
	// rrweb's own input observer skips these (its default block and ignore
	// classes; `rrweb.js` `initInputObserver`), so this must too.
	if (field.closest('.rr-block') || field.classList.contains('rr-ignore')) return;
	const id = rrweb.record.mirror.getId(field);
	if (id === -1) return;
	emitRecorderEvent({
		type: rrweb.EventType.IncrementalSnapshot,
		data: {source: rrweb.IncrementalSource.Input, text: recordedInputValue(field.value, field, false), isChecked: false, id},
		timestamp: Date.now(),
	});
}

/** Routes where we must not record — their DOMs are session-replay viewers
 *  (or related tooling) whose huge serialized trees break the recorder. */
const NO_RECORD_PATH_PREFIXES = ['/session-replay'];

function isNoRecordRoute(): boolean {
	return NO_RECORD_PATH_PREFIXES.some(p => window.location.pathname.startsWith(p));
}

function persistSessionState(): void {
	if (sessionId) {
		sessionStorage.setItem(recordingKey(SS_SESSION_ID), sessionId);
		sessionStorage.setItem(recordingKey(SS_CHUNK_INDEX), String(chunkIndex));
	}
}

function clearPersistedSession(): void {
	sessionStorage.removeItem(recordingKey(SS_SESSION_ID));
	sessionStorage.removeItem(recordingKey(SS_CHUNK_INDEX));
}

/** How long a visit survives with no activity in ANY tab before the next
 *  recording starts a new one. Matches INACTIVITY_TIMEOUT so a visit and a
 *  session agree on where one sitting ends and the next begins. */
const VISIT_IDLE_TIMEOUT = 30 * 60 * 1000;

/** Don't write to localStorage on every scroll tick. */
const VISIT_ACTIVITY_WRITE_THROTTLE_MS = 60 * 1000;

/** The visit this tab's session belongs to. Sent when creating a session so
 *  the list can group a multi-tab sitting into one row (OWA-664). */
let visitId: string | null = null;
let lastVisitActivityWrite = 0;

function newUuid(): string {
	if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
		return crypto.randomUUID();
	}
	// Older WebKit only exposes randomUUID on secure origins. A visit id is a
	// grouping key, never a security token, so a random fallback is fine.
	const bytes = new Uint8Array(16);
	crypto.getRandomValues(bytes);
	bytes[6] = (bytes[6] & 0x0f) | 0x40;
	bytes[8] = (bytes[8] & 0x3f) | 0x80;
	const hex = Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('');
	return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

/**
 * Join the visit already in progress on this origin, or start a new one.
 *
 * localStorage is shared by every tab on the origin, so a second tab opened
 * while the user is working joins the first tab's visit instead of looking
 * like a separate sitting. Each tab still records its OWN session and its own
 * rrweb stream — only the grouping is shared.
 */
function acquireVisitId(): {id: string; isNew: boolean} {
	try {
		const existing = localStorage.getItem(recordingKey(LS_VISIT_ID));
		const lastActivity = parseInt(localStorage.getItem(recordingKey(LS_VISIT_LAST_ACTIVITY)) || '', 10);
		if (existing && Number.isFinite(lastActivity) && Date.now() - lastActivity < VISIT_IDLE_TIMEOUT) {
			touchVisitActivity(true);
			return {id: existing, isNew: false};
		}
		const fresh = newUuid();
		localStorage.setItem(recordingKey(LS_VISIT_ID), fresh);
		touchVisitActivity(true);
		return {id: fresh, isNew: true};
	}
	catch {
		// localStorage can throw (Safari private browsing, storage disabled).
		// Fall back to a tab-local visit: grouping degrades to one-row-per-tab,
		// which is exactly today's behaviour, and nothing else breaks.
		return {id: newUuid(), isNew: true};
	}
}

/** Push the visit's activity clock forward. Throttled unless `force`. */
function touchVisitActivity(force = false): void {
	const now = Date.now();
	if (!force && now - lastVisitActivityWrite < VISIT_ACTIVITY_WRITE_THROTTLE_MS) return;
	lastVisitActivityWrite = now;
	try {
		localStorage.setItem(recordingKey(LS_VISIT_LAST_ACTIVITY), String(now));
	}
	catch {
		// Non-fatal; see acquireVisitId().
	}
}

/** Close a session after 30 minutes of no user interaction. */
const INACTIVITY_TIMEOUT = 30 * 60 * 1000;

/** Keep quiet sessions well inside the server's stale-session window. */
const HEARTBEAT_IDLE_MS = 4 * 60 * 1000;

/** Scheduled flush cadence. Larger = fewer requests but more delay before
 *  events land on the server. 30s gives ~2 chunks/min on a busy session,
 *  down from 12/min at the previous 5s cadence. */
const FLUSH_INTERVAL_MS = 30_000;

/** Eager-flush threshold. When the buffer reaches this many events between
 *  scheduled flushes, kick a flush immediately so a busy page doesn't grow
 *  one giant chunk (and so the server sees events sooner than 30s later).
 *  200 rrweb events ≈ 50–150KB uncompressed — a comfortable chunk size. */
const EAGER_FLUSH_EVENT_COUNT = 200;

/** Minimum gap between eager flushes (OWA-838). Under a DOM mutation storm
 *  (a dashboard re-rendering 8,000 calendar nodes every few seconds) the
 *  200-event trigger fired several times a second and one tab wrote 2,500
 *  chunks an hour, 13 million events a day. Spacing eager flushes out
 *  bounds the chunk count at 12 a minute; the events themselves still land,
 *  just in larger chunks, and the 30-second scheduled flush is unchanged. */
const EAGER_FLUSH_MIN_GAP_MS = 5_000;

let eagerFlushQueued = false;

/** When the last chunk upload was attempted. Paces eager flushes only. */
let lastFlushStartedAt = 0;

/** Queue a one-shot flush, no sooner than EAGER_FLUSH_MIN_GAP_MS after the
 *  previous one. Guarded so multiple emit() calls in a row coalesce into a
 *  single scheduled flush. */
function scheduleEagerFlush(): void {
	if (eagerFlushQueued || flushing) return;
	eagerFlushQueued = true;
	const wait = Math.max(0, lastFlushStartedAt + EAGER_FLUSH_MIN_GAP_MS - Date.now());
	setTimeout(() => {
		eagerFlushQueued = false;
		flushBuffer().catch(err => console.warn('Session replay: eager flush failed', err));
	}, wait);
}

function createReplayTransport(firmId: string, userId: string, operation: SessionOperation): SessionReplayTransport {
	const assertOwner = () => {
		operation.assertCurrent();
		const identity = tabSession.getIdentity();
		if (identity?.firmId !== firmId || identity.userId !== userId) throw new SessionChangedError();
	};
	return new SessionReplayTransport({
		baseUrl: URI,
		getCredential: expected => {
			assertOwner();
			if (expected.firmId !== firmId || expected.userId !== userId) return null;
			const peeked = tabSession.peekCredentials();
			const token = peeked && serverCredential(peeked);
			return replayCredentialMatchesScope(token, expected) ? token : null;
		},
		renewCredential: async (expected, signal) => {
			assertOwner();
			if (expected.firmId !== firmId || expected.userId !== userId) throw new SessionChangedError();
			if (signal.aborted) throw signal.reason;
			await tabSession.credentials(operation);
			operation.assertCurrent();
			assertOwner();
		},
	});
}

function notifyRecordingFailure(value: unknown): void {
    if (value instanceof ReplayTransportError && value.retryable) return;
    const error = value instanceof Error ? value : new Error('Session replay could not be saved.');
	recordingFailure = error;
	if (stopFn) {
		stopFn();
		stopFn = null;
	}
	window.dispatchEvent(new CustomEvent('outlaw:session-replay-error', {detail: {
		message: 'Session recording paused because it could not be saved. Retained recording data will be retried.',
	}}));
}

function surfaceReplayError(): void {
	window.dispatchEvent(new CustomEvent('outlaw:session-replay-error', {detail: {
		message: 'A retained session recording could not be recovered. New recording continues.',
	}}));
}

async function resumeRecorderAfterRecovery(): Promise<void> {
	if (!recordingFailure || preservationHold || stopping || retiringSession || !runtime || !captureQueue ||
		captureQueue.isInterrupted) return;
	const slots = await runtime.chunkSlotUsage();
	if (slots.total >= REPLAY_SESSION_SOFT_CHUNK_SLOTS || slots.bytes >= REPLAY_SESSION_SOFT_BYTES) return;
	recordingFailure = null;
	if (!recordingPaused && !stopFn && tabSession.isLocalReady()) startRrwebRecorder();
}

/** Persist every synchronously accepted event before uploading immutable chunks. */
async function flushBuffer(keepalive = false, requireUpload = false): Promise<void> {
	if (preservationHold && !requireUpload) return;
	if (!runtime || !captureQueue) return;
	if (flushPromise) {
		const requestedRuntime = runtime;
		await flushPromise;
		// Events may become durable while the prior upload is unwinding, and a
		// rollover may replace that upload's runtime. Drain again if the runtime
		// this caller requested is still active.
		if (runtime === requestedRuntime) return flushBuffer(keepalive, requireUpload);
		return;
	}
	const activeRuntime = runtime;
	const activeQueue = captureQueue;
	let task: Promise<void>;
	task = (async () => {
		flushing = true;
		lastFlushStartedAt = Date.now();
		try { await retryRetiringSession(); }
		catch (error) { console.warn('Session replay: rolled session remains queued for retry', error); }
		const before = await activeRuntime.localStatus();
		const recovering = activeQueue.isInterrupted;
		if (recovering) await activeQueue.retry();
		else await activeQueue.flush();
		await activeRuntime.drain({keepalive});
		const after = await activeRuntime.localStatus();
		chunkIndex = after.nextChunkIndex;
		if (after.nextChunkIndex !== before.nextChunkIndex) lastServerContact = Date.now();
		const slots = await activeRuntime.chunkSlotUsage();
		if (runtime === activeRuntime && (slots.total >= REPLAY_SESSION_SOFT_CHUNK_SLOTS - 1 || slots.bytes >= REPLAY_SESSION_SOFT_BYTES)) {
			await ensureRollover(activeRuntime, slots.total, slots.bytes);
		}
		if (recordingFailure && slots.total < REPLAY_SESSION_SOFT_CHUNK_SLOTS && slots.bytes < REPLAY_SESSION_SOFT_BYTES &&
			!preservationHold && !retiringSession && runtime === activeRuntime) {
			if (recovering) activeQueue.resumeAfterRecovery();
			recordingFailure = null;
			if (!recordingPaused && !stopFn && tabSession.isLocalReady()) startRrwebRecorder();
		}
	})().finally(() => {
		flushing = false;
		if (flushPromise === task) flushPromise = null;
	});
	flushPromise = task;
	return task;
}

/**
 * Tell the server this session is still alive.
 *
 * The server closes any session whose `updated_at` has gone stale, and
 * `updated_at` only advances on chunk upload. A user who is reading, on a
 * call, or sitting on a backgrounded tab generates no events and therefore no
 * uploads, so without this the session gets closed underneath them.
 *
 * A 404 means the session was already closed — recycle into a new one rather
 * than heartbeating a corpse.
 */
async function sendHeartbeat(): Promise<void> {
	if (!runtime || preservationHold || recordingFailure) return;
	try {
		await runtime.heartbeat();
		lastServerContact = Date.now();
	}
	catch (error) {
		notifyRecordingFailure(error);
		console.warn('Session replay: heartbeat failed; durable state retained', error);
	}
}

async function flushIntervalTick(): Promise<void> {
	if (preservationHold || !runtime) return;
	if (!recoveryPromise && runtimeOperation) startBackgroundRecovery(runtime.descriptor, runtime.scope.sessionId, runtimeOperation);
	await flushBuffer();
	if (Date.now() - lastServerContact >= HEARTBEAT_IDLE_MS) await sendHeartbeat();
}

async function abandonActiveHandle(keepRecoveryPointer = false): Promise<void> {
	const recovery = recoveryPromise;
	recoveryController?.abort(new DOMException('Replay recorder closed.', 'AbortError'));
	recoveryController = null;
	if (stopFn) {
		stopFn();
		stopFn = null;
	}
	if (rolloverPromise) {
		try { await rolloverPromise; }
		catch { /* The retained source session remains durable. */ }
	}
	const retiring = retiringSession;
	if (retiring) {
		try { await retryRetiringSession(); }
		catch { /* A durable retired stream can be recovered by the next page. */ }
		if (retiringSession === retiring && retiring.queue.pendingCount === 0) {
			retiring.runtime.close();
			await retiring.claim?.release();
			retiringSession = null;
		}
	}
	if (recovery) {
		try { await recovery; }
		catch { /* Recovery failures leave the exact state for the next worker. */ }
	}
	if (flushInterval) clearInterval(flushInterval);
	flushInterval = null;
	document.removeEventListener('visibilitychange', handleVisibilityChange);
	window.removeEventListener('beforeunload', handleBeforeUnload);
	window.removeEventListener('focus', handleWindowFocus);
	window.removeEventListener('blur', emitWindowFocusState);
	document.removeEventListener('focusout', handleFieldLeft, true);
	stopInactivityWatch();
	runtime?.close();
	runtime = null;
	runtimeOperation = null;
	captureQueue = null;
	sessionId = null;
	chunkIndex = 0;
	flushing = false;
	flushPromise = null;
	const claim = activeSessionClaim;
	activeSessionClaim = null;
	await claim?.release();
	if (!keepRecoveryPointer) clearPersistedSession();
}

/**
 * After an inactivity close, wait for the user to come back and then start a
 * fresh session. A new session after 30+ idle minutes is the correct outcome —
 * that gap is exactly what separates one session from the next — but without
 * this the tab would simply stop recording for good.
 */
function armRestartOnActivity(): void {
	if (restartArmed || preservationHold) return;
	restartArmed = true;

	const onActivity = () => {
		disarmRestartOnActivity();
		// Activity inside the replay viewer (or while an in-app caller holds the
		// pause) can't start a session — startRecording() would refuse. Stay
		// armed instead of silently consuming the one shot.
		if (recordingPaused || isNoRecordRoute()) {
			armRestartOnActivity();
			return;
		}
		if (!recordingUserId || !recordingFirmId) return;
		startRecording(recordingUserId, recordingFirmId)
			.catch(err => console.warn('Session replay: restart after inactivity failed', err));
	};
	restartListener = onActivity;

	for (const event of ACTIVITY_EVENTS) {
		document.addEventListener(event, onActivity, {passive: true, capture: true, once: true});
	}
}

function disarmRestartOnActivity(): void {
	if (restartListener) {
		for (const event of ACTIVITY_EVENTS) {
			document.removeEventListener(event, restartListener, true);
		}
		restartListener = null;
	}
	restartArmed = false;
}

/** Reset the inactivity timer on user interaction */
function resetInactivityTimer(): void {
	// Same interactions keep the VISIT alive for other tabs, so a second tab
	// opened while the user works here joins this visit rather than starting
	// its own. Throttled to one localStorage write a minute.
	touchVisitActivity();
	if (inactivityTimer) clearTimeout(inactivityTimer);
	inactivityTimer = setTimeout(() => {
		console.info('Session replay: closing session after 30 minutes of inactivity');
		// stopRecording() disarms any pending restart, so arm afterwards.
		stopRecording()
			.catch(() => {})
			.finally(() => armRestartOnActivity());
	}, INACTIVITY_TIMEOUT);
}

const ACTIVITY_EVENTS = ['mousedown', 'keydown', 'scroll', 'touchstart'] as const;

function startInactivityWatch(): void {
	for (const event of ACTIVITY_EVENTS) {
		document.addEventListener(event, resetInactivityTimer, {passive: true, capture: true});
	}
	resetInactivityTimer();
}

function stopInactivityWatch(): void {
	if (inactivityTimer) {
		clearTimeout(inactivityTimer);
		inactivityTimer = null;
	}
	for (const event of ACTIVITY_EVENTS) {
		document.removeEventListener(event, resetInactivityTimer, true);
	}
}

/**
 * Flush on page hide / tab close. Uses keepalive so the browser doesn't drop
 * the request when the page is being unloaded.
 */
function handleVisibilityChange(): void {
	if (document.visibilityState === 'hidden') {
		// Tab switch — flush buffered events but keep the session alive.
		// Use keepalive=false: the page is still alive during tab switches,
		// so a regular fetch completes normally. Keepalive is only needed
		// for beforeunload where the page is actually going away.
		flushBuffer(false).catch(err => console.warn('Session replay: visibilitychange flush failed', err));
	}
}

function handleBeforeUnload(): void {
	// Page unload (close tab, navigate away, refresh) — flush but keep session alive.
	// The session will be resumed on next page load, or ended by the server-side
	// inactivity timeout if the user doesn't return.
	//
	flushBuffer(true).catch(err => console.warn('Session replay: beforeunload flush failed', err));
}

/**
 * Attach rrweb's recorder to the current page. Shared by startRecording()
 * (initial session start) and resumeRecording() (coming back from a pause).
 */
function startRrwebRecorder(): void {
	if (preservationHold || stopping || recordingPaused || recordingFailure || !runtime || !captureQueue) return;
	const operation = recordingOperation();
	const emit = (event: eventWithTime): void => {
		if (preservationHold || operation.signal.aborted || !tabSession.isLocalReady()) return;
		if (snapshotBoundaryEvents) {
			snapshotBoundaryEvents.push(event);
			return;
		}
		if (recordingFailure) return;
		const target = captureQueue;
		if (!target) return;
		target.enqueue(event);
		if (target.pendingCount >= EAGER_FLUSH_EVENT_COUNT) scheduleEagerFlush();
	};
	emitRecorderEvent = emit;
	const recorderStop = rrweb.record({
		emit,
		// Emit a new FullSnapshot every 5 minutes so playback can recover
		// even if early chunks are lost.
		checkoutEveryNms: 5 * 60 * 1000,
		// Drop mousemove capture entirely — it's the single largest source of
		// events (one record per animation frame while the cursor is in
		// motion) and contributes the bulk of network volume. Clicks,
		// scrolls, input, and DOM mutations are still recorded, so playback
		// still shows what the user did; the cursor just won't glide between
		// clicks. `mouseInteraction: true` (default) keeps click/hover
		// events. If we later want pointer trails back, switch this to a
		// number (e.g. 100) to throttle instead of disabling.
		// rrweb records a field on `change` instead of on every `input` event,
		// so native typing no longer produces one event per keystroke (John,
		// q-20260927-192931-x2bg). Values written by code are still recorded
		// through rrweb's value-setter hook; `maskInputFn` below and
		// `handleFieldLeft` cover what this setting does not.
		sampling: {
			mousemove: false,
			input: 'last',
		},
		// OWA-693: this MUST stay true, and it does NOT mean "mask everything".
		//
		// rrweb only consults `maskInputFn` for input types present in its
		// internal `maskInputOptions` map, and it derives that map from this
		// flag: `false` yields `{password: true}` — nothing else — so
		// `maskInputFn` is never invoked for a text input and the policy below
		// becomes dead code. (rrweb 2.0.1: `rrweb.js` builds the map; the gate
		// is `maskInputValue()` in `rrweb-snapshot.js`.) That is exactly what
		// happened between OWA-675 and OWA-693: every `data-private` field —
		// SSN, Tax ID, DOB, bank routing/account numbers — recorded verbatim.
		//
		// `true` opts EVERY input type into the masking pipeline, which is the
		// only way our per-element policy gets asked at all. Selectivity then
		// comes from `maskInputFn`, which returns the text unchanged for any
		// field `shouldMaskInput()` does not claim.
		//
		// Regression test: e2e/OWA-675-application-replay-masking.spec.ts
		// gunzips a real replay chunk and asserts the SSN is absent while a
		// control field's value is present.
		maskAllInputs: true,
		// OWA-877: rrweb 2.0.1 only calls `maskTextFn` for text whose element
		// matches `maskTextSelector` (`serializeTextNode` and the
		// characterData branch of `processMutation` in `rrweb.js`), so the
		// selector matches every element and `maskTextFn` makes the decision,
		// the same way `maskAllInputs` hands inputs to `maskInputFn`.
		// data-private text keeps rrweb's own mask (every non-space character
		// becomes `*`); all other text goes through the redactor. rrweb's
		// maskTextClass accepts only a single class string; the Select--private
		// wrapper (the only className-based privacy marker in this codebase) is
		// already paired with data-private on the same element.
		maskTextSelector: '*',
		maskTextFn: (text, element) => element?.closest('[data-private]') ? text.replace(/[\S]/g, '*') : redactString(text),
		// Selectively mask input VALUES in the recording (does NOT affect what
		// the user sees on screen). The policy — and the reasoning behind each
		// rule — lives in sessionReplayMasking.ts, so that forms collecting
		// sensitive data can unit-test it against their own rendered DOM.
		// Values the policy does not mask still go through the redactor, but
		// only once the user has left the field (John,
		// q-20260927-192931-x2bg): while a text field has focus its value is
		// recorded as asterisks, and `handleFieldLeft` records it again after
		// focus leaves. `sampling.input: 'last'` alone is not enough, because
		// React rewrites the `value` attribute on every keystroke and rrweb
		// records that attribute change through this function too, and the
		// redactor cannot recognize a partly typed number. A native select
		// changes while it still has focus and never holds a partial value, so
		// it is exempt.
		maskInputFn: (text, element) => recordedInputValue(text, element,
			element === element.ownerDocument.activeElement && element.tagName !== 'SELECT'),
		plugins: [createConsoleRecordPlugin()],
	});
	stopFn = recorderStop;
	// rrweb emits Meta/FullSnapshot synchronously before returning its stop
	// function. If that emission interrupted persistence, notifyRecordingFailure
	// could not stop the recorder yet. Stop it now and keep its exact queue for
	// retry; delayed callbacks are rejected by the operation lifetime.
	if (recordingFailure || operation.signal.aborted) {
		recorderStop?.();
		if (stopFn === recorderStop) stopFn = null;
		return;
	}

	// Which tab the user is looking at, for the tab-following player (OWA-669).
	// Same function references every time, so re-attaching after a pause does
	// not stack duplicate listeners.
	window.addEventListener('focus', handleWindowFocus);
	window.addEventListener('blur', emitWindowFocusState);
	document.addEventListener('focusout', handleFieldLeft, true);
	emitWindowFocusState();
}

function makeRuntime(
	descriptor: ReplayCreationDescriptor, operation: SessionOperation,
	onInterrupted: (error: Error) => void = notifyRecordingFailure,
): SessionReplayRuntime {
	return new SessionReplayRuntime({
		descriptor,
		transport: createReplayTransport(descriptor.firmId, descriptor.userId, operation),
		lifetimeSignal: operation.signal,
		onInterrupted,
	});
}

function isAuthoritativeDisposal(error: unknown): boolean {
	return error instanceof ReplayTransportError &&
		['resume_session_missing', 'session_deleted'].includes(error.serverCode ?? '');
}

/** Finish an abandoned stream without ever attaching this tab's new rrweb
 * events to it. The Web Lock proves no live document still owns the stream. */
async function finishRetained(state: ReplayCaptureState, operation: SessionOperation, signal?: AbortSignal): Promise<void> {
	const claim = await claimSession(state.sessionId);
	if (!claim) return;
	const candidate = makeRuntime(state.descriptor, operation, () => {});
	try {
		operation.assertCurrent();
		const local = await candidate.localCompletionState();
		if (!state.confirmed && local.totalEvents === 0) {
			await candidate.discardLocal();
			return;
		}
		// complete() consumes a durable terminal receipt locally before it ever
		// activates. This keeps a server-side retention/deletion policy from
		// stranding already-completed local state on a later reload.
		await candidate.complete(state.summary, {signal});
	}
	catch (error) {
		if (!isAuthoritativeDisposal(error)) throw error;
		await candidate.discardLocal();
		surfaceReplayError();
	}
	finally {
		candidate.close();
		await claim.release();
	}
}

async function createFreshRuntime(
	descriptor: Omit<ReplayCreationDescriptor, 'sessionId'>, operation: SessionOperation,
	persistPointer = true,
): Promise<{created: SessionReplayRuntime; claim: SessionClaim}> {
	const fresh = {...descriptor, sessionId: newUuid()};
	const claim = await claimSession(fresh.sessionId, true);
	if (!claim) throw new Error('The recording could not claim its tab session.');
	if (persistPointer) sessionStorage.setItem(recordingKey(SS_SESSION_ID), fresh.sessionId);
	let created!: SessionReplayRuntime;
	created = makeRuntime(fresh, operation, error => {
		if (runtime === created || runtime === null) notifyRecordingFailure(error);
	});
	try {
		await created.activate();
		return {created, claim};
	}
	catch (error) {
		// The descriptor is already durable. A retryable create failure must
		// not prevent capture into this next part while storage is unavailable.
		if (error instanceof ReplayTransportError && error.retryable) return {created, claim};
		if (isAuthoritativeDisposal(error)) await created.discardLocal();
		created.close();
		await claim.release();
		throw error;
	}
}

function retryRetiringSession(): Promise<void> {
	const retiring = retiringSession;
	if (!retiring) return Promise.resolve();
	if (retiring.retry) return retiring.retry;
	const task = (async () => {
		if (retiring.queue.isInterrupted) await retiring.queue.retry();
		else await retiring.queue.flush();
		// Closing intent is local and durable. Remote completion belongs to the
		// recovery worker, so a slow earlier request cannot hold up the next cap.
		await retiring.runtime.beginCompletion(retiring.summary);
		retiring.runtime.close();
		await retiring.claim?.release();
		if (retiringSession === retiring) retiringSession = null;
		if (runtime && runtimeOperation && !recoveryPromise) startBackgroundRecovery(runtime.descriptor, runtime.scope.sessionId, runtimeOperation);
		await resumeRecorderAfterRecovery();
	})().finally(() => {
		if (retiring.retry === task) retiring.retry = undefined;
	});
	retiring.retry = task;
	return task;
}

async function rolloverActiveRuntime(expected: SessionReplayRuntime): Promise<void> {
	if (runtime !== expected || retiringSession || !runtimeOperation || !recordingFirmId || !recordingUserId || !visitId) return;
	const operation = runtimeOperation;
	const descriptor = {
		firmId: recordingFirmId, userId: recordingUserId, initialUrl: window.location.href,
		userAgent: navigator.userAgent, viewportWidth: window.innerWidth,
		viewportHeight: window.innerHeight, visitId: expected.descriptor.visitId,
		continuationOf: expected.descriptor.continuationOf ?? expected.scope.sessionId,
		partIndex: (expected.descriptor.partIndex ?? 0) + 1,
	};
	let successor: SessionReplayRuntime | undefined;
	let successorClaim: SessionClaim | undefined;
	const sourceQueue = captureQueue;
	const sourceWasInterrupted = sourceQueue?.isInterrupted;
	const boundary: unknown[] = [];
	const pausedFailure = recordingFailure;
	let restartedRecorder = false;
	let switched = false;
	try {
		({created: successor, claim: successorClaim} = await createFreshRuntime(descriptor, operation, false));
		if (!successor || !successorClaim) return;
		if (runtime !== expected || operation.signal.aborted) return;
		if (!sourceWasInterrupted && sourceQueue?.isInterrupted && recordingFailure !== pausedFailure) return;

		// A capacity failure stops rrweb so retained work cannot grow without
		// bound. Restart it only after the successor is confirmed and divert its
		// synchronous initial snapshot. The recorder's emit closure follows the
		// module queue after the switch, so later events land in the successor.
		// A live rollover takes an in-place boundary and therefore has no gap.
		if (stopFn) {
			snapshotBoundaryEvents = boundary;
			try { rrweb.record.takeFullSnapshot(true); }
			catch (error) {
				console.warn('Session replay: rollover snapshot failed; continuing the current recorder', error);
				return;
			}
			finally { snapshotBoundaryEvents = null; }
		} else {
			recordingFailure = null;
			snapshotBoundaryEvents = boundary;
			try {
				startRrwebRecorder();
				if (!stopFn) throw new Error('Session replay recorder did not restart.');
				restartedRecorder = true;
			}
			catch (error) {
				console.warn('Session replay: rollover recorder restart failed; retained data remains paused', error);
				return;
			}
			finally { snapshotBoundaryEvents = null; }
		}
		if (boundary.length < 2 || (boundary[0] as {type?: unknown})?.type !== 4 ||
			(boundary[1] as {type?: unknown})?.type !== 2) return;

		const oldRuntime = expected;
		const oldQueue = sourceQueue;
		const oldClaim = activeSessionClaim;
		if (!oldQueue || runtime !== oldRuntime) return;
		const nextRuntime = successor;
		const successorQueue = new ReplayCaptureQueue(events => appendCaptured(nextRuntime, events), notifyRecordingFailure);
		const oldSummary = buildAccumulatedSummary();
		runtime = nextRuntime;
		captureQueue = successorQueue;
		activeSessionClaim = successorClaim;
		sessionId = nextRuntime.scope.sessionId;
		chunkIndex = 0;
		successor = undefined;
		successorClaim = undefined;
		resetAccumulator();
		persistSessionState();
		recordingFailure = null;
		for (const event of boundary) successorQueue.enqueue(event);
		retiringSession = {runtime: oldRuntime, queue: oldQueue, claim: oldClaim, summary: oldSummary};
		switched = true;
		void retryRetiringSession().catch(error =>
			console.warn('Session replay: rolled session remains queued for retry', error));
	}
	finally {
		snapshotBoundaryEvents = null;
		if (!switched && restartedRecorder) {
			stopFn?.();
			stopFn = null;
		}
		if (!switched) recordingFailure ??= pausedFailure;
		if (!switched && boundary.length && sourceQueue) {
			for (const event of boundary) sourceQueue.enqueue(event);
		}
		if (successor) {
			try { await successor.beginCompletion(); }
			catch (error) { console.warn('Session replay: unused successor remains queued for recovery', error); }
			finally { successor.close(); }
		}
		await successorClaim?.release();
	}
}

async function ensureRollover(expected: SessionReplayRuntime, total: number, bytes = 0): Promise<void> {
	if (runtime !== expected || stopping) return;
	if (!rolloverPromise) {
		const task = rolloverActiveRuntime(expected).finally(() => {
			if (rolloverPromise === task) rolloverPromise = null;
		});
		rolloverPromise = task;
	}
	try { await rolloverPromise; }
	catch (error) { console.warn('Session replay: successor could not be prepared', error); }
	if (runtime === expected && (total >= REPLAY_SESSION_SOFT_CHUNK_SLOTS || bytes >= REPLAY_SESSION_SOFT_BYTES)) {
		notifyRecordingFailure(new Error('Session recording reached its offline capacity and is paused while retained data is saved.'));
	}
}

async function appendCaptured(target: SessionReplayRuntime, events: readonly unknown[]): Promise<void> {
	await target.append(events);
	if (runtime !== target || stopping) return;
	const slots = await target.chunkSlotUsage();
	if (slots.total >= REPLAY_SESSION_SOFT_CHUNK_SLOTS - 1 || slots.bytes >= REPLAY_SESSION_SOFT_BYTES) await ensureRollover(target, slots.total, slots.bytes);
}

/** Exact-account recovery is deliberately independent of startup. The active
 * recorder owns its own lock and starts before this paged worker touches old
 * state, so a large backlog cannot delay fresh capture. */
function startBackgroundRecovery(
	identity: Pick<ReplayCreationDescriptor, 'firmId' | 'userId'>,
	activeId: string, operation: SessionOperation,
): void {
	recoveryController?.abort(new DOMException('Replay recovery replaced.', 'AbortError'));
	const controller = new AbortController();
	recoveryController = controller;
	const abort = () => controller.abort(operation.signal.reason);
	operation.signal.addEventListener('abort', abort, {once: true});
	let task: Promise<void>;
	task = (async () => {
		const store = new ReplayStateStore();
		try {
			let after = '';
			for (;;) {
				if (controller.signal.aborted) return;
				const page = await store.pending(identity, after, 20);
				if (!page.length) return;
				for (const state of page) {
					if (controller.signal.aborted) return;
					if (state.sessionId === activeId) continue;
					try { await finishRetained(state, operation, controller.signal); }
					catch (error) {
						if (controller.signal.aborted) return;
						console.warn('Session replay: retained recording remains queued for recovery', error);
					}
				}
				if (page.length < 20) return;
				after = page[page.length - 1].sessionId;
			}
		}
		finally { store.close(); }
	})().finally(() => {
		operation.signal.removeEventListener('abort', abort);
		if (recoveryController === controller) recoveryController = null;
		if (recoveryPromise === task) recoveryPromise = null;
	});
	recoveryPromise = task;
}

/**
 * Start recording a session for the given user and firm.
 * Creates a session on the server, then starts rrweb recording with privacy settings.
 */
export async function startRecording(userId: string, firmId: string): Promise<void> {
	if (!tabSession.isLocalReady() || preservationHold || starting || stopping) return;
	if (sessionId) {
		if (recordingUserId !== userId || recordingFirmId !== firmId) return;
		let generationChanged = false;
		try { runtimeOperation?.assertCurrent(); }
		catch { generationChanged = true; }
		if (generationChanged && runtime && captureQueue) {
			const replacementOperation = tabSession.captureRequest();
			try {
				const retiring = retiringSession;
				if (retiring?.retry) {
					// Its old generation is already cancelled. Settle that attempt
					// before replacing the runtime that owns the same durable scope.
					try { await retiring.retry; }
					catch { /* The exact retained state is retried below. */ }
				}
				if (retiringSession === retiring && retiring) {
					if (retiring.queue.isInterrupted) await retiring.queue.retry();
					else await retiring.queue.flush();
					const replacementRetiring = makeRuntime(retiring.runtime.descriptor, replacementOperation);
					retiring.runtime.close();
					retiring.runtime = replacementRetiring;
					retiring.queue = new ReplayCaptureQueue(
						events => appendCaptured(replacementRetiring, events), notifyRecordingFailure,
					);
				}
				if (captureQueue.isInterrupted) await captureQueue.retry();
				else await captureQueue.flush();
				const descriptor = runtime.descriptor;
				runtime.close();
				const replacement = makeRuntime(descriptor, replacementOperation);
				runtime = replacement;
				runtimeOperation = replacementOperation;
				captureQueue = new ReplayCaptureQueue(events => appendCaptured(replacement, events), notifyRecordingFailure);
				await replacement.activate();
				await replacement.drain();
				recordingFailure = null;
				startBackgroundRecovery({firmId, userId}, replacement.scope.sessionId, replacementOperation);
				void retryRetiringSession().catch(error =>
					console.warn('Session replay: reauthenticated rolled session remains queued for retry', error));
			}
			catch (error) {
				notifyRecordingFailure(error);
				if (!(error instanceof ReplayTransportError && error.retryable)) return;
			}
		}
		if (recordingFailure && !preservationHold && !stopFn) {
			try { await stopRecording(); }
			catch { return; }
		}
		if (recordingUserId === userId && recordingFirmId === firmId && !stopFn && !recordingPaused && !recordingFailure) {
			if (sessionId) startRrwebRecorder();
		}
		if (sessionId) return;
	}
	const operation = tabSession.captureRequest();
	if (operation.identity.firmId !== firmId || operation.identity.userId !== userId) return;
	const epoch = recordingEpoch;
	const assertCurrent = () => {
		operation.assertCurrent();
		if (epoch !== recordingEpoch) throw new SessionChangedError();
	};
	recordingUserId = userId;
	recordingFirmId = firmId;
	if (recordingPaused || isNoRecordRoute()) return;
	disarmRestartOnActivity();
	starting = true;
	try {
		const visit = acquireVisitId();
		visitId = visit.id;
		const {created: nextRuntime, claim} = await createFreshRuntime({
			firmId, userId, initialUrl: window.location.href, userAgent: navigator.userAgent,
			viewportWidth: window.innerWidth, viewportHeight: window.innerHeight, visitId, partIndex: 0,
		}, operation);
		runtime = nextRuntime;
		activeSessionClaim = claim;
		runtimeOperation = operation;
		assertCurrent();
		sessionId = nextRuntime.scope.sessionId;
		captureQueue = new ReplayCaptureQueue(events => appendCaptured(nextRuntime, events), notifyRecordingFailure);
		recordingFailure = null;
		const local = await nextRuntime.localStatus();
		assertCurrent();
		chunkIndex = local.nextChunkIndex;
		lastServerContact = Date.now();
		resetAccumulator();
		persistSessionState();
		startRrwebRecorder();
		setTimeout(() => {
			void flushBuffer().catch(error => console.warn('Session replay: initial flush retained for retry', error));
		}, 500);
		flushInterval = setInterval(() => {
			void flushIntervalTick().catch(error => console.warn('Session replay: interval flush failed', error));
		}, FLUSH_INTERVAL_MS);
		document.addEventListener('visibilitychange', handleVisibilityChange);
		window.addEventListener('beforeunload', handleBeforeUnload);
		startInactivityWatch();
		startBackgroundRecovery({firmId, userId}, nextRuntime.scope.sessionId, operation);
	}
	catch (error) {
		console.warn('Session replay: start failed', error);
		await abandonActiveHandle(true);
	}
	finally { starting = false; }
}

/**
 * Stop recording. Flushes the remaining buffer and marks the session as ended.
 * Snapshots the auth header up front so the final flush succeeds even if
 * localStorage is cleared during logout.
 */
export async function stopRecording(requireUpload = false): Promise<void> {
	if (preservationHold && !requireUpload) return;
	if (stopping) {
		if (requireUpload) throw new Error('A recording upload is already finishing. Try logging out again shortly.');
		return;
	}
	recordingEpoch++;
	recoveryController?.abort(new DOMException('Replay recording is stopping.', 'AbortError'));
	recoveryController = null;
	disarmRestartOnActivity();
	if (!runtime || !sessionId || !captureQueue) return;
	if (requireUpload) preservationHold = true;
	stopping = true;
	if (stopFn) {
		stopFn();
		stopFn = null;
	}
	if (flushInterval) clearInterval(flushInterval);
	flushInterval = null;
	document.removeEventListener('visibilitychange', handleVisibilityChange);
	window.removeEventListener('beforeunload', handleBeforeUnload);
	window.removeEventListener('focus', handleWindowFocus);
	window.removeEventListener('blur', emitWindowFocusState);
	document.removeEventListener('focusout', handleFieldLeft, true);
	stopInactivityWatch();
	try {
		if (rolloverPromise) await rolloverPromise;
		await retryRetiringSession();
		if (!runtime || !captureQueue) return;
		const activeRuntime = runtime;
		const activeQueue = captureQueue;
		while (flushPromise) await flushPromise;
		if (activeQueue.isInterrupted) await activeQueue.retry();
		else await activeQueue.flush();
		await activeRuntime.complete(buildAccumulatedSummary());
		activeRuntime.close();
		runtime = null;
		runtimeOperation = null;
		captureQueue = null;
		sessionId = null;
		chunkIndex = 0;
		recordingFailure = null;
		preservationHold = false;
		recordingPaused = false;
		const claim = activeSessionClaim;
		activeSessionClaim = null;
		await claim?.release();
		clearPersistedSession();
	}
	catch (error) {
		notifyRecordingFailure(error);
		if (requireUpload) throw new Error('The recording has not finished saving.', {cause: error});
		// Inactivity failures retain the in-memory capture queue, runtime and
		// exclusive Web Lock. The next activity retries this exact close before
		// any fresh stream is allowed to start.
		persistSessionState();
		throw error;
	}
	finally { stopping = false; }
}

/**
 * Pause rrweb emission without ending the server-side session. Keeps
 * sessionId, chunkIndex, and the flush interval alive so resumeRecording()
 * continues uploading into the same session. Use this when entering a route
 * whose DOM we don't want to record (e.g. the replay viewer itself).
 */
export function pauseRecording(): void {
	if (recordingPaused) return;
	recordingPaused = true;
	if (!sessionId) return; // Nothing to tear down yet; flag alone is enough
	if (stopFn) {
		stopFn();
		stopFn = null;
	}
	flushBuffer(false).catch(err => console.warn('Session replay: pause flush failed', err));
}

/**
 * Resume rrweb emission into the currently-active session. If the session is
 * still open, rrweb re-attaches and emits a fresh FullSnapshot that playback
 * uses to rebuild the DOM after the pause gap. If recording never started,
 * this just clears the pause flag so future startRecording() calls proceed.
 */
export function resumeRecording(): void {
	if (preservationHold) return;
	const wasPaused = recordingPaused;
	recordingPaused = false;
	if (!tabSession.isLocalReady()) return;
	if (sessionId && wasPaused && !stopFn) {
		startRrwebRecorder();
		return;
	}
	// The session may have died while paused (server reaped it during a long
	// stay in the replay viewer), or recording was refused outright because the
	// tab loaded directly onto a no-record route. Now that emission is allowed
	// again, start fresh rather than leaving the tab unrecorded until the next
	// full reload. startRecording() safely refuses if this is still the wrong
	// place or a session somehow exists.
	if (!sessionId && wasPaused && recordingUserId && recordingFirmId) {
		// No preemptive disarm: startRecording() disarms itself once it actually
		// proceeds, and if it refuses (still on a no-record route) any armed
		// restart must survive to catch the user's next interaction.
		startRecording(recordingUserId, recordingFirmId)
			.catch(err => console.warn('Session replay: restart after pause failed', err));
	}
}

/**
 * Test-only hooks. Do not import from production code. Exported so unit tests
 * can seed and inspect module-level singleton state without invoking the
 * full rrweb/fetch pipeline.
 */
export const __test__ = {
	isPaused: () => recordingPaused,
	getSessionId: () => sessionId,
	getChunkIndex: () => chunkIndex,
	getBufferLength: () => captureQueue?.pendingCount ?? 0,
	getRetiringSessionId: () => retiringSession?.runtime.scope.sessionId ?? null,
	getRetiringBufferLength: () => retiringSession?.queue.pendingCount ?? 0,
	retryRetiringSession: () => retryRetiringSession(),
	isPreservationHeld: () => preservationHold,
	getFlushWaitTimeout: () => 30_000,
	pushEvent: (e: object) => { captureQueue?.enqueue(e); },
	flushBuffer: (keepalive = false) => flushBuffer(keepalive),
	setSessionId: (id: string | null) => { sessionId = id; },
	setPaused: (v: boolean) => { recordingPaused = v; },
	isRestartArmed: () => restartArmed,
	getLastServerContact: () => lastServerContact,
	setLastServerContact: (t: number) => { lastServerContact = t; },
	setCredentials: (userId: string | null, firmId: string | null) => {
		recordingUserId = userId;
		recordingFirmId = firmId;
	},
	sendHeartbeat: () => sendHeartbeat(),
	flushIntervalTick: () => flushIntervalTick(),
	scheduleEagerFlush: () => scheduleEagerFlush(),
	getEagerFlushMinGapMs: () => EAGER_FLUSH_MIN_GAP_MS,
	setLastFlushStartedAt: (t: number) => { lastFlushStartedAt = t; },
	armRestartOnActivity: () => armRestartOnActivity(),
	getInactivityTimeout: () => INACTIVITY_TIMEOUT,
	getHeartbeatIdleMs: () => HEARTBEAT_IDLE_MS,
	getVisitIdleTimeout: () => VISIT_IDLE_TIMEOUT,
	getVisitId: () => visitId,
	waitForRecovery: () => recoveryPromise ?? Promise.resolve(),
	// Persist rrweb's synchronous queue without initiating a network drain.
	// This exposes the durable/network boundary to recovery tests.
	flushCaptureQueue: () => captureQueue?.flush() ?? Promise.resolve(),
	acquireVisitId: () => acquireVisitId().id,
	acquireVisit: () => acquireVisitId(),
	claimSession: async (id: string) => {
		const claim = await claimSession(id);
		if (!claim) return false;
		await claim.release();
		return true;
	},
	forceRollover: async () => {
		if (runtime) await rolloverActiveRuntime(runtime);
	},
	simulateReload: async () => {
		disarmRestartOnActivity();
		await abandonActiveHandle(true);
		visitId = null;
		lastVisitActivityWrite = 0;
		recordingPaused = false;
		preservationHold = false;
		recordingFailure = null;
		stopFn = null;
		flushing = false;
		flushPromise = null;
		eagerFlushQueued = false;
		lastFlushStartedAt = 0;
		recordingUserId = null;
		recordingFirmId = null;
		lastServerContact = 0;
		stopping = false;
		starting = false;
		rolloverPromise = null;
		snapshotBoundaryEvents = null;
		recordingEpoch++;
	},
	reset: async () => {
		disarmRestartOnActivity();
		await abandonActiveHandle();
		visitId = null;
		lastVisitActivityWrite = 0;
		recordingPaused = false;
		preservationHold = false;
		recordingFailure = null;
		stopFn = null;
		chunkIndex = 0;
		flushing = false;
		flushPromise = null;
		eagerFlushQueued = false;
		lastFlushStartedAt = 0;
		recordingUserId = null;
		recordingFirmId = null;
		lastServerContact = 0;
		stopping = false;
		starting = false;
		rolloverPromise = null;
		snapshotBoundaryEvents = null;
	},
};
